import { useRef } from 'react';
import { useInView } from 'framer-motion';
// @mui
import { Box } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
//
import { BISQUE, STAGE } from '../../../theme/stage';
import { CSS_EASE } from '../motion';
import { dropIn, entrance } from './entrance';

// ----------------------------------------------------------------------

export type RingSize = 'mark' | 'menu' | 'hero' | 'closing' | 'art';

export type RingEnter = 'load' | 'view';

type Props = {
  size: RingSize;
  state?: 'filled' | 'empty';
  enter?: RingEnter;
  sx?: SxProps<Theme>;
};

const SIZE = {
  mark: { position: 'relative', display: 'block', width: 12, height: 12 },
  menu: {
    position: 'relative',
    display: 'inline-block',
    width: '.55em',
    height: '.55em',
    mx: '.03em',
    verticalAlign: '-.012em',
  },
  hero: { position: 'absolute', display: 'block', inset: 0 },
  closing: {
    position: 'relative',
    display: 'block',
    fontSize: { xs: 157, sm: 186, lg: 214 },
    width: '.56em',
    height: '.56em',
  },
  art: { position: 'relative', display: 'block', width: '100%', height: '100%' },
} as const;

const STATE = {
  filled: {
    boxShadow: BISQUE.contact,
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: '50%',
      backgroundImage: BISQUE.torus,
      maskImage: BISQUE.hole,
      WebkitMaskImage: BISQUE.hole,
    },
  },
  empty: { border: `1.5px solid ${STAGE.lineStrong}` },
};

const ENTER = {
  load: entrance(dropIn, { duration: 900, delay: 300, ease: CSS_EASE.settle }),
  view: {
    opacity: 0,
    transform: 'translateY(-24%)',
    transition: `opacity 600ms linear, transform 900ms ${CSS_EASE.settle}`,
    '&[data-in="true"]': { opacity: 1, transform: 'none' },
  },
};

export default function BisqueRing({ size, state = 'filled', enter, sx }: Props) {
  const ref = useRef<HTMLSpanElement>(null);

  const inView = useInView(ref, { once: true, amount: 0.25 });

  return (
    <Box
      ref={ref}
      component="span"
      aria-hidden
      data-enter={enter ? true : undefined}
      data-in={enter === 'view' ? String(inView) : undefined}
      sx={[
        {
          ...SIZE[size],
          ...STATE[state],
          flexShrink: 0,
          borderRadius: '50%',
          pointerEvents: 'none',
          ...(enter && ENTER[enter]),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
}
